<template>
  <div class="jim-scheduling-container">

    <!-- ✅ FULL-WIDTH WRAPPER -->
    <div class="logistics-page">

    <h1 class="page-heading">Logistics Scheduling</h1>

    <!-- ✅ MAIN LAYOUT: LEFT BUTTON PANEL + RIGHT SCHEDULER -->
    <div class="logistics-layout">

      <!-- ✅ LEFT SIDE BUTTON PANEL (like JIMScheduling) -->
      <div class="left-side-section">

        
        <!-- Ship Date picker -->
        <div class="control">
          <label>Start Date</label>
          <input type="date" v-model="selectedShipDt" @change="loadAll" />
        </div>

        <!-- Buttons -->
        <button class="plan-it-btn" @click="loadAll" :disabled="loading">
          Refresh
        </button>

        <button class="plan-it-btn" @click="runLogistics" :disabled="loading">
          Ship it Pablo
        </button>

        <button class="plan-it-btn pablotify-btn" @click="$refs.pablotify.openPopup()">
          Comm Center
          <span v-if="pablotifyUnread > 0" class="notif-badge">
            🔔 {{ pablotifyUnread }}
          </span>
        </button>



        <PabloQuacious
          ref="pablotify"
          :dept="'Logistics'"
          :loc="selectedLocation"
          :showButton="false"
          @unread-count="pablotifyUnread = $event"
        />


        <!-- Save removed per your note -->
        <!-- <button class="plan-it-btn" @click="saveChanges" :disabled="loading || !hasUnsavedChanges">Save</button> -->

      </div>

      <!-- ✅ RIGHT SIDE: SUMMARY + TRUCKS + UNPLANNED -->
      <div class="scheduler-wrapper">

        <!-- Summary bar -->
        <div class="summary-bar" v-if="summary">
          <div class="summary-item">
            <div class="summary-label">Trucks</div>
            <div class="summary-value">{{ summary.trucks.total_trucks }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Locked</div>
            <div class="summary-value">{{ summary.trucks.locked_trucks }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Utilization</div>
            <div class="summary-value">{{ summary.trucks.utilization_pct }}%</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Scheduled Orders</div>
            <div class="summary-value">{{ summary.scheduled.scheduled_orders }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Unplanned Orders</div>
            <div class="summary-value">{{ summary.unplanned.unplanned_orders }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Unplanned Pallets</div>
            <div class="summary-value">{{ formatNum(summary.unplanned.unplanned_pallets) }}</div>
          </div>
        </div>

        <!-- ✅ TRUCKS + UNPLANNED SIDE BY SIDE (this prevents unplanned from getting huge) -->
        <div class="scheduler-grid">

          <!-- LEFT COLUMN: Trucks -->
          <div class="machines-section">

            <div
              class="trucks-scroll-container"
              ref="trucksScrollEl"
              @dragover="onTrucksDragOver"
            >
              <div class="day-box">

                <div style="margin-bottom: 12px;">
                  <div style="font-size: 14px; margin-bottom: 4px;">
                    Ship Date: <strong>{{ selectedShipDt }}</strong>
                    — Location: <strong>{{ selectedLocation }}</strong>
                  </div>

                  <div style="font-size: 12px; opacity: 0.8;">
                    <span v-if="loading">Loading…</span>
                    <span v-else>
                      Trucks: {{ trucks.length }}
                      Orders Scheduled: {{ scheduledOrdersCount }}
                    </span>
                  </div>
                </div>

                <!-- Truck Rows -->
                <div>
                  <div
                    v-for="truck in trucks"
                    :key="truck.truck_id"
                    class="truck-row"
                    :class="{ locked: truck.locked }"
                  >
                    <!-- Truck Header -->
                    <div class="truck-header">
                      <div class="truck-id">
                        {{ truck.truck_id }}
                      </div>

                      <div class="truck-meta">
                        <span class="pill">
                          Pallets: {{ formatNum(truck.pallets_used) }} / {{ formatNum(truck.max_pallets) }}
                        </span>
                        <span class="pill">Stops: {{ truck.stops || 0 }}</span>
                        <span class="pill" v-if="truck.locked">🔒 Locked</span>
                      </div>

                      <div class="truck-actions">

                        
                        <!-- Draft indicator -->
                        <span
                          v-if="pendingTrucks && pendingTrucks[truck.truck_id] && !truck.locked"
                          class="pill"
                        >
                          ✳️ Draft
                        </span>

                        <!-- Truck Details -->
                        <button
                          class="btn btn-small"
                          @click="openTruckModal(truck)"
                          :disabled="loading"
                        >
                          Details
                        </button>
                        <!-- Lock icon -->
                        <button
                          class="lock-btn"
                          :class="truck.locked ? 'lock-red' : 'lock-green'"
                          @click="onLockClicked(truck)"
                          :disabled="loading"
                          title="Locking saves and prevents future changes"
                        >
                          {{ truck.locked ? '🔒' : '🔓' }}
                        </button>


                      </div>
                    </div>

                    <!-- Load Bar -->
                    <div class="truck-load-bar">
                      <div class="truck-load-fill" :style="{ width: truckFillPct(truck) + '%' }"></div>
                    </div>

                    <!-- Orders in truck -->
                    <draggable
                      v-model="truck.orders"
                      :group="{ name: 'salesOrders', pull: true, put: canDrop(truck) }"
                      item-key="ord_no"
                      :animation="150"
                      ghost-class="wo-ghost"
                      chosen-class="wo-chosen"
                      drag-class="wo-drag"
                      @start="onOrderDragStart"
                      @end="onOrderDragEnd"
                    >
                      <template #item="{ element }">
                        <div
                          class="order-card-compact"
                          :data-ord-no="element.ord_no"
                          :data-from-truck="truck.truck_id"
                          :data-pallets="element.pallets"
                          @click="openOrderModal(element)"
                        >
                          <!-- LEFT -->
                          <div class="order-left">
                            <span class="ord-no">#{{ element.ord_no }}</span>
                            <span class="dot">•</span>
                            <span class="city">{{ element.city }}, {{ element.state }}</span>
                          </div>

                          <!-- CENTER -->
                          <div class="order-center" @click.stop @mousedown.stop>
                            <span class="dot">•</span>

                            <input
                              class="order-note-input"
                              type="text"
                              v-model="element.order_note"
                              :disabled="truck.locked"
                              placeholder="note..."
                              maxlength="40"
                              @input="onOrderNoteInput(element, 'truck')"
                            />
                            
                            <span class="dot">•</span>
                          </div>

                          <!-- RIGHT -->
                          <div class="order-right">
                            <span class="pallets">{{ formatNum(element.pallets) }} pallets</span>

                            <span v-if="element.ship_block_code" class="ship-flag">
                              {{ element.ship_block_code }}
                            </span>
                          </div>
                        </div>

                      </template>

                      <template #footer>
                        <div v-if="truck.orders.length === 0" class="empty-truck">
                          (empty)
                        </div>
                      </template>
                    </draggable>

                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- RIGHT COLUMN: Unplanned Orders -->
          <div class="unscheduled-section">

            <div
              class="unplanned-panel"
              ref="unplannedScrollEl"
              @dragover="onUnplannedDragOver"
            >
              <div class="unplanned-header">
                <h2>Unplanned Orders</h2>
                <div class="small-muted">{{ unplannedOrders.length }} orders</div>
              </div>

              <draggable
                v-model="unplannedOrders"
                :group="{ name: 'salesOrders', pull: true, put: true }"
                item-key="ord_no"
                :animation="150"
                ghost-class="wo-ghost"
                chosen-class="wo-chosen"
                drag-class="wo-drag"
                @start="onUnplannedDragStart"
                @end="onUnplannedDragEnd"
                class="unplanned-list"
              >
                <template #item="{ element }">
                  <div
                    class="unplanned-card"
                    :data-ord-no="element.ord_no"
                    :data-from-unplanned="'true'"
                    :data-pallets="element.pallets"
                    @click="openOrderModal(element)"
                  >
                    <div class="unplanned-top">
                      <div class="ord-no">#{{ element.ord_no }}</div>
                      <div class="pallets">{{ formatNum(element.pallets) }} pallets</div>
                    </div>

                    <div class="unplanned-mid">
                      <div class="cust">{{ element.customer_name }}</div>
                      <div class="city">{{ element.city }}, {{ element.state }}</div>
                    </div>

                    

                    <div class="unplanned-bottom">
                      <span class="chip danger">
                        {{ formatReason(element.reason_code) }}
                      </span>
                      <span class="chip">Via: {{ element.ship_via_cd }}</span>
                      <!-- ✅ Ship-block badge (Option A: show only problems) -->
                        <span
                          v-if="element.ship_block_code"
                          class="ship-flag"
                        >
                          {{ element.ship_block_code }}
                        </span>

                    </div>
                  </div>
                </template>

                <template #footer>
                  <div v-if="unplannedOrders.length === 0" class="empty-unplanned">
                    No unplanned orders 🎉
                  </div>
                </template>
              </draggable>

            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
            <PabloChatSidebar
                :loc="selectedLocation"
                
              />
  </div>

  <!-- ✅ TRUCK DETAILS MODAL -->
  <div v-if="showTruckModal" class="popup-overlay" @click.self="closeTruckModal">
    <div class="popup-content">
      <div class="popup-header">
        <h3>Truck Details</h3>
        <button class="close-btn" @click="closeTruckModal">✕</button>
      </div>

      <div v-if="selectedTruck" style="text-align: left;">
        <div style="margin-bottom: 10px;">
          <strong>{{ selectedTruck.truck_id }}</strong>
        </div>

        <div style="margin-bottom: 8px; font-size: 13px; opacity: 0.9;">
          <div><strong>Ship Date:</strong> {{ selectedTruck.ship_dt }}</div>
          <div><strong>Location:</strong> {{ selectedTruck.loc }}</div>
          <div>
            <strong>Blocked Orders:</strong>
            {{ (selectedTruck.orders || []).filter(o => o.ship_block_code).length }}
          </div>
        </div>

        <div style="margin-bottom: 8px;">
          <span class="pill">
            Pallets: {{ formatNum(selectedTruck.pallets_used) }} / {{ formatNum(selectedTruck.max_pallets) }}
          </span>
          <span class="pill" style="margin-left: 8px;">
            Stops: {{ selectedTruck.stops || 0 }}
          </span>
          <span class="pill" style="margin-left: 8px;" v-if="selectedTruck.locked">
            🔒 Locked
          </span>
        </div>

        <table class="popup-table" style="margin-top: 10px;">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Customer</th>
              <th>City</th>
              <th>Pallets</th>
              <th>Ship Via</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in selectedTruck.orders" :key="o.ord_no">
              <td>#{{ o.ord_no }}</td>
              <td>{{ o.customer_name }}</td>
              <td>{{ o.city }}, {{ o.state }}</td>
              <td>{{ formatNum(o.pallets) }}</td>
              <td>{{ o.ship_via_cd }}</td>
              <td>
                <span v-if="o.ship_block_code" class="ship-flag">
                  {{ o.ship_block_code }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="!selectedTruck.orders || selectedTruck.orders.length === 0" style="margin-top: 0.75rem;">
          <em>No orders on this truck.</em>
        </div>
      </div>
    </div>
  </div>

    <!-- ✅ ORDER DETAILS MODAL -->
    <div v-if="showOrderModal" class="popup-overlay" @click.self="closeOrderModal">
      <div class="popup-content">
        <div class="popup-header">
          <div class="modal-title-wrap">
            <div class="modal-title-line">
              <h3 style="margin: 0;">Order #{{ selectedOrder?.ord_no }}</h3>

              <span
                v-if="selectedOrder?.ship_block_code"
                class="ship-flag"
                style="margin-left: 10px;"
              >
                {{ selectedOrder.ship_block_code }}
              </span>
            </div>

            <div class="modal-subtitle">
              {{ selectedOrder?.customer_name }}
            </div>
          </div>

          <div style="margin-top: 6px;">
            <button
              class="btn btn-small"
              @click="showCustomerNotesModal = true"
              :disabled="customerOrderNotesLoading"
            >
              Standing Notes
            </button>

            <span v-if="customerOrderNotesLoading" class="small-muted" style="margin-left: 8px;">
              Loading…
            </span>

            <span
              v-else-if="customerOrderNotes?.StandingOrderNotes"
              class="pill"
              style="margin-left: 8px;"
            >
              Notes Found
            </span>

            <span
              v-else
              class="small-muted"
              style="margin-left: 8px;"
            >
              No Notes
            </span>
          </div>

          <button class="close-btn" @click="closeOrderModal">✕</button>
        </div>

        <div v-if="selectedOrder" style="text-align: left;">

          <div class="order-meta">
            <div><strong>Destination:</strong> {{ selectedOrder.city }}, {{ selectedOrder.state }}</div>
            <div><strong>Pallets:</strong> {{ formatNum(selectedOrder.pallets) }}</div>
            <div v-if="selectedOrder.ord_status === 'C'" class="hold-status">
              <strong>Status:</strong>&nbsp;HOLD
            </div>

            <div v-if="selectedOrder.orig_req_ship_dt">
              <strong>Original Req Ship:</strong> {{ formatDateOnly(selectedOrder.orig_req_ship_dt) }}
            </div>

            <div v-if="selectedOrder.drift_bd && selectedOrder.drift_bd !== 0">
              <strong>Drift (BD):</strong> {{ selectedOrder.drift_bd }}
            </div>

            <div>
              <strong>Ship Ready:</strong>&nbsp;
              <span v-if="selectedOrder.ship_ready === false" class="small-muted">No</span>
              <span v-else class="small-muted">Yes</span>
            </div>

            <div v-if="selectedOrder.reason_code">
              <strong>Reason:</strong> {{ selectedOrder.reason_code }}
            </div>
          </div>

          <!-- Optional mini summary pills -->
          <div style="margin-top: 10px;">
            <span class="pill">Pallets: {{ formatNum(selectedOrder.pallets) }}</span>
            <span class="pill" style="margin-left: 8px;">Via: {{ selectedOrder.ship_via_cd }}</span>
          </div>

          <!-- ✅ Patch 6B.3: Item-level detail table -->
          <div class="modal-divider"></div>

          <div v-if="orderDetailsLoading" class="small-muted">
            Loading item details…
          </div>

          <table v-else class="popup-table" style="margin-top: 10px;">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty To Ship</th>
                <th>On Hand</th>
                <th>Short</th>
                <th>Action</th> <!-- ✅ NEW -->
              </tr>
            </thead>

            <tbody>
              <tr v-for="l in orderDetailsLines" :key="l.item_no">
                <td>{{ l.item_no }}</td>
                <td>{{ formatNum(l.qty_to_ship) }}</td>
                <td>{{ formatNum(l.qty_on_hand) }}</td>
                <td>
                  <span v-if="l.shortage > 0" class="ship-flag">
                    {{ formatNum(l.shortage) }}
                  </span>
                  <span v-else class="small-muted">0</span>
                </td>

                <!-- ✅ NEW ACTION CELL -->
                <td>
                  <button
                    v-if="l.shortage > 0"
                    class="btn btn-small"
                    @click="askProductionForItem(l)"
                  >
                    Ask Production
                  </button>
                  <span v-else class="small-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!orderDetailsLoading && orderDetailsLines.length === 0" style="margin-top: 0.75rem;">
            <em>No item lines found for this order.</em>
          </div>

        </div>

        <!-- ✅ CUSTOMER STANDING NOTES MODAL (Correct Place: inside Order Modal, outside selectedOrder block) -->
        <div
          v-if="showCustomerNotesModal"
          class="popup-overlay"
          @click.self="showCustomerNotesModal = false"
        >
          <div class="popup-content" style="max-width: 700px;">
            <div class="popup-header">
              <h3 style="margin: 0;">Standing Order Notes</h3>
              <button class="close-btn" @click="showCustomerNotesModal = false">✕</button>
            </div>

            <div style="text-align: left; margin-top: 10px;">

              <div v-if="customerOrderNotesLoading" class="small-muted">
                Loading customer notes…
              </div>

              <div v-else-if="customerOrderNotes?.StandingOrderNotes" style="white-space: pre-wrap;">
                {{ customerOrderNotes.StandingOrderNotes }}
              </div>

              <div v-else class="small-muted">
                No standing order notes found for this customer.
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>

</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import PabloChatSidebar from './PabloChatSidebar.vue'
import PabloQuacious from './PabloQuacious.vue';

const REASON_LABELS = {
  BELOW_MIN_TRUCK_PALLETS: "Below minimum pallets",
  MISSING_PALLET_FACTOR: "No pallet factor for this item",
};


export default {
  components: { draggable, PabloChatSidebar, PabloQuacious },

  name: 'LogisticsPage',
  data() {
    return {
      selectedShipDt: this.todayISO(),
      loading: false,
      pendingTrucks: {},

      // Raw API rows
      truckRows: [],
      trucks: [],
      unplannedOrders: [],
      summary: null,

      showTruckModal: false,
      selectedTruck: null,

      showOrderModal: false,
      selectedOrder: null,

      orderDetailsLoading: false,
      orderDetailsLines: [],

      pablotifyUnread: 0,

      noteSaveTimers: {},        // ord_no -> timeout
      noteSaving: {},            // ord_no -> true/false
      noteSaveError: {},         // ord_no -> error string

      customerOrderNotesLoading: false,
      customerOrderNotes: null,
      showCustomerNotesModal: false,



      // UI state
      hasUnsavedChanges: false,
      lockedToastCooldown: false
    };
  },

  computed: {
    // ✅ matches JIMScheduling: pull location from root (App.vue)
    selectedLocation() {
      return this.$root.selectedLocation;
    },

   

    scheduledOrdersCount() {
      let count = 0;
      for (const t of (this.trucks || [])) {
        count += (t.orders || []).length;
      }
      return count;
    }

  },

  watch: {
    // ✅ just like JIMScheduling: react to global location changes
    selectedLocation() {
      this.clearUi();
      this.loadAll();
    }
  },

  methods: {
    todayISO() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    formatNum(n) {
      if (n === null || n === undefined) return '';
      const x = Number(n);
      if (Number.isNaN(x)) return n;
      return x.toFixed(2).replace(/\.00$/, '');
    },

    formatDateOnly(val) {
      if (!val) return '';
      try {
        return String(val).slice(0, 10);  // "2025-12-30"
      } catch (e) {
        return val;
      }
    },

    
    formatReason(reason) {
      if (!reason) return "";
      return REASON_LABELS[reason] || reason.replace(/_/g, " ").toLowerCase();
    },


    truckFillPct(truck) {
      const used = Number(truck.pallets_used || 0);
      const max = Number(truck.max_pallets || 0);
      if (!max) return 0;
      return Math.max(0, Math.min(100, (used / max) * 100));
    },

    clearUi() {
      this.truckRows = [];
      this.unplannedOrders = [];
      this.summary = null;
      this.hasUnsavedChanges = false;
    },

    buildTrucksFromRows(rows) {
      const map = new Map();

      for (const r of rows) {
        if (!map.has(r.truck_id)) {
          map.set(r.truck_id, {
            truck_id: r.truck_id,
            loc: r.loc,
            ship_dt: r.ship_dt,
            truck_seq: r.truck_seq,
            locked: r.locked,
            locked_by: r.locked_by,
            locked_ts: r.locked_ts,
            max_pallets: r.max_pallets,
            pallets_used: r.pallets_used,
            stops: r.stops,
            cubes_used: r.cubes_used,
            footage_used: r.footage_used,
            last_run_id: r.last_run_id,
            updated_at: r.updated_at,
            order_note: r.order_note,
            order_note_saved: r.order_note,
            orders: []
          });
        }

        if (r.ord_no) {
          map.get(r.truck_id).orders.push({
            ord_no: r.ord_no,
            ship_dt: r.ship_dt,
            stop_seq: r.stop_seq,
            pallets: r.pallets,
            cubes_calc: r.cubes_calc,
            cubes_hdr: r.cubes_hdr,
            footage_hdr: r.footage_hdr,
            customer_id: r.customer_id,
            customer_name: r.customer_name,
            city: r.city,
            state: r.state,
            zip5: r.zip5,
            zcta5: r.zcta5,
            lat: r.lat,
            lon: r.lon,
            ship_via_cd: r.ship_via_cd,
            orig_req_ship_dt: r.orig_req_ship_dt,
            drift_bd: r.drift_bd,
            ord_status: r.ord_status,
            ship_ready: r.ship_ready,
            ship_block_code: r.ship_block_code,
            reason_code: r.reason_code,
            order_note: r.order_note,
            order_note_saved: r.order_note
          });
        }
      }

      // ✅ Sort stops inside each truck by stop_seq
      for (const truck of map.values()) {
        truck.orders.sort((a, b) => (a.stop_seq ?? 999999) - (b.stop_seq ?? 999999));
      }


      // ✅ Correct sorting (ship_dt, then truck_seq)
      return Array.from(map.values()).sort((a, b) => {
        const d = String(a.ship_dt).localeCompare(String(b.ship_dt));
        if (d !== 0) return d;
        return Number(a.truck_seq || 0) - Number(b.truck_seq || 0);
      });
    },


    canDrop(truck) {
      return !truck.locked;
    },

    onOrderDragStart(evt) {
      const item = evt.item;
      const ord_no = item?.dataset?.ordNo;

      console.log('[Logistics] Drag start:', {
        ord_no,
        fromTruck: item?.dataset?.fromTruck,
        pallets: item?.dataset?.pallets
      });
    },

    async onOrderDragEnd(evt) {
      try {
        const item = evt.item;

        const ord_no = item?.dataset?.ordNo;
        const fromTruck = item?.dataset?.fromTruck;

        // ✅ Detect if dropped into UNPLANNED panel
        const droppedIntoUnplanned = evt.to?.closest?.('.unplanned-panel');

        // ✅ Always derive ship_dt from Vue state, NOT from DOM dataset
        let ship_dt = null;

        if (fromTruck) {
          const fromTruckObj = (this.trucks || []).find(t => t.truck_id === fromTruck);

          if (fromTruckObj) {
            // ✅ Find the actual order object on that truck
            const orderObj = (fromTruckObj.orders || []).find(o => String(o.ord_no) === String(ord_no));

            // ✅ Pull ship_dt from order first, fallback to truck header
            ship_dt = orderObj?.ship_dt || fromTruckObj.ship_dt || null;
          }
        }

        // Final fallback
        if (!ship_dt) ship_dt = this.selectedShipDt;

        // Normalize ship_dt (YYYY-MM-DD)
        ship_dt = String(ship_dt).slice(0, 10);

        console.log("DRAG END DATA:", {
          ordNo: ord_no,
          fromTruck,
          ship_dt
        });

        // ✅ Move Truck → Unplanned
        if (droppedIntoUnplanned) {
          console.log(`[Logistics] Move ord ${ord_no} from ${fromTruck} → UNPLANNED`);

          // ✅ Validate
          if (!ord_no || !fromTruck) {
            console.warn('[Logistics] Missing ord_no or fromTruck for move to unplanned.');
            await this.loadAll();
            return;
          }

          // ✅ Check locked source truck in state
          const fromTruckObj = (this.trucks || []).find(t => t.truck_id === fromTruck);
          if (fromTruckObj?.locked) {
            alert('This truck is locked. Orders cannot be moved out.');
            await this.loadAll();
            return;
          }

          // ✅ Call backend route to persist move
          await axios.post('/setOrderToUnplanned', {
            location: this.selectedLocation,
            ship_dt,
            ord_no,
            reason_code: 'MANUAL_UNPLANNED'
          });

          // ✅ Reload from DB to ensure UI matches persisted state
          await this.loadAll();
          return;
        }

        // ------------------------
        // ✅ ELSE: regular Truck → Truck move
        // ------------------------

        // Identify TO truck using closest truck-row container
        const toTruckRow = evt.to?.closest?.('.truck-row');
        const toTruckId = toTruckRow
          ? toTruckRow.querySelector('.truck-id')?.innerText?.trim()
          : null;

        if (!ord_no || !toTruckId) {
          console.log('[Logistics] Invalid drop location.');
          return;
        }

        // If dropped back into same truck, do nothing
        if (fromTruck === toTruckId) {
          console.log(`[Logistics] Reordered within ${toTruckId}. No backend update.`);
          return;
        }

        // ✅ Use REAL state now
        const fromTruckObj = (this.trucks || []).find(t => t.truck_id === fromTruck);
        if (fromTruckObj?.locked) {
          alert('This truck is locked. Orders cannot be moved out.');
          await this.loadAll();
          return;
        }

        const toTruckObj = (this.trucks || []).find(t => t.truck_id === toTruckId);
        if (toTruckObj?.locked) {
          alert('This truck is locked. Orders cannot be moved into it.');
          await this.loadAll();
          return;
        }

        console.log(`[Logistics] Move ord ${ord_no} from ${fromTruck} → ${toTruckId}`);

        // ✅ Mark both trucks as "draft"
        if (!this.pendingTrucks) this.pendingTrucks = {};
        if (fromTruck) this.pendingTrucks[fromTruck] = true;
        if (toTruckId) this.pendingTrucks[toTruckId] = true;

        this.hasUnsavedChanges = true;

      } catch (err) {
        console.error('onOrderDragEnd error:', err);
        await this.loadAll();
      }
    },

    onUnplannedDragStart(evt) {
      console.log('[Logistics] Drag start from Unplanned:', evt?.item?.dataset?.ordNo);
    },

    onUnplannedDragEnd(evt) {
      try {
        const item = evt.item;

        const ord_no = item?.dataset?.ordNo || item?.dataset?.ord_no;
        const pallets = Number(item?.dataset?.pallets || 0);

        console.log("[Logistics] Drag end from Unplanned:", { ord_no, pallets });

        // Identify the target truck via drop container
        const toTruckEl = evt.to?.closest?.(".truck-row");
        if (!toTruckEl) {
          console.log("[Logistics] Drop not into a truck row. No action.");
          return;
        }

        const truckIdEl = toTruckEl.querySelector(".truck-id");
        const toTruckId = truckIdEl ? truckIdEl.innerText.trim() : null;

        if (!toTruckId) {
          console.error("[Logistics] Could not identify target truck_id");
          return;
        }

        // ✅ Find the truck in REAL state
        const toTruck = (this.trucks || []).find(t => t.truck_id === toTruckId);

        if (!toTruck) {
          console.error("[Logistics] Target truck not found in trucks state:", toTruckId);
          return;
        }

        if (toTruck.locked) {
          alert("That truck is locked. Cannot add orders.");
          return;
        }

        // ✅ Mark truck as draft (has unsaved changes)
        if (!this.pendingTrucks) this.pendingTrucks = {};
        this.pendingTrucks[toTruck.truck_id] = true;

        // ✅ Remove from unplanned list in UI
        const idx = this.unplannedOrders.findIndex(o => String(o.ord_no) === String(ord_no));
        if (idx !== -1) {
          this.unplannedOrders.splice(idx, 1);
        }

        console.log(`[Logistics] Unplanned order ${ord_no} moved into ${toTruckId} (draft)`);

      } catch (err) {
        console.error("onUnplannedDragEnd error:", err);
      }
    },

    onOrderNoteInput(order, source) {
      // ✅ Debounce per order number
      const key = `${source}-${order.ord_no}`;

      if (this.noteSaveTimers[key]) {
        clearTimeout(this.noteSaveTimers[key]);
      }

      this.noteSaveTimers[key] = setTimeout(() => {
        this.saveOrderNote(order, source);
      }, 500); // 500ms debounce feels right
    },

    async saveOrderNote(order, source) {
      const key = `${source}-${order.ord_no}`;

      this.noteSaving[key] = true;
      this.noteSaveError[key] = null;

      try {
        console.log('[saveOrderNote] order passed in:', {
          ord_no: order?.ord_no,
          ship_dt: order?.ship_dt,
          truck_id: order?.truck_id,
          note: order?.order_note
        });

        const ship_dt = String(order.ship_dt || '').slice(0, 10);

        if (!ship_dt) {
          console.warn('[saveOrderNote] Missing ship_dt on order, refusing to save:', order);
          return;
        }

        const payload = {
          loc: this.selectedLocation,
          ship_dt,                      // ✅ USE ORDER SHIP DATE
          ord_no: String(order.ord_no).trim(),
          order_note: order.order_note || '',
          source
        };

        console.log('[saveOrderNote] ship_dt chosen:', {
          selectedShipDt: this.selectedShipDt,
          orderShipDt: order.ship_dt,
          payloadShipDt: payload.ship_dt
        });


        console.log('[saveOrderNote] payload:', payload);
        await this.$axios.post('/setOrderNote', payload);
        order.order_note_saved = order.order_note;

        this.noteSaving[key] = false;

      } catch (err) {
        console.error('saveOrderNote error:', err);

        this.noteSaving[key] = false;

        const status = err?.response?.status;
        const data = err?.response?.data || {};

        // ✅ Locked truck (409) — show ONE toast (cooldown) + revert note
        if (status === 409 && data.code === 'TRUCK_LOCKED') {
          // revert UI to last saved value
          order.order_note = order.order_note_saved || '';

          // toast cooldown to avoid spam
          if (!this.lockedToastCooldown) {
            this.lockedToastCooldown = true;
            this.$toast?.warning?.('Truck is locked. Unlock it to edit notes.');
            setTimeout(() => {
              this.lockedToastCooldown = false;
            }, 2500);
          }

          return;
        }

        // ✅ Permission errors (403) - match your existing style
        if (status === 403) {
          this.$toast?.error?.(data?.error || 'Insufficient rights.');
          return;
        }

        // ✅ Anything else
        this.$toast?.error?.(data?.error || err?.message || 'Failed to save note');
      }



      
    },





    async loadAll() {
      if (!this.selectedLocation) return;

      await Promise.all([
        this.loadTruckSchedule(),
        this.loadUnplannedOrders(),
        this.loadSummary()
      ]);
    },

    async loadTruckSchedule() {
      this.loading = true;
      try {
        const { data } = await axios.get('/getTruckSchedule', {
          params: {
            location: this.selectedLocation,
            start_dt: this.selectedShipDt,
            window_days: 14
          }
        });

        this.truckRows = data;

        // ✅ IMPORTANT: build mutable UI state from raw rows
        this.trucks = this.buildTrucksFromRows(data);

      } catch (err) {
        console.error('loadTruckSchedule error:', err);
        alert('Failed to load truck schedule.');
      } finally {
        this.loading = false;
      }
    },

    async loadUnplannedOrders() {
      try {
        const { data } = await axios.get('/getUnplannedOrders', {
          params: {
            location: this.selectedLocation,
            start_dt: this.selectedShipDt,
            window_days: 14
          }

        });
        this.unplannedOrders = data;
      } catch (err) {
        console.error('loadUnplannedOrders error:', err);
        alert('Failed to load unplanned orders.');
      }
    },

    async loadSummary() {
      try {
        const { data } = await axios.get('/getTruckScheduleSummary', {
          params: {
            location: this.selectedLocation,
            start_dt: this.selectedShipDt,
            window_days: 14
          }

        });
        this.summary = data;
      } catch (err) {
        console.error('loadSummary error:', err);
        this.summary = null;
      }
    },

    async toggleTruckLock(truck) {
      try {
        const newLocked = !truck.locked;

        await axios.post('/setTruckLock', {
          location: this.selectedLocation,
          truck_id: truck.truck_id,
          locked: newLocked
        });

        await this.loadAll();
      } catch (err) {
        console.error('toggleTruckLock error:', err);
        alert('Failed to toggle lock.');
      }
    },

    async runLogistics() {
      try {
        this.loading = true;

        const loc = String(this.selectedLocation || '')
          .trim()
          .toUpperCase()
          .slice(0, 1);

        const ok = confirm(
          `Run Logistics Engine?\n\n` +
          `Location: ${loc}\n` +
          `Start Date: ${this.selectedShipDt}\n` +
          `Window: 14 shipping days\n\n` +
          `This will rebuild ALL unlocked trucks in the window.\nLocked trucks will remain unchanged.`
        );

        if (!ok) return;

        await axios.post('/runLogisticsEngine', {
          location: loc,
          start_dt: this.selectedShipDt,
          window_days: 14
        });

        await this.loadAll();
      } catch (err) {
        console.error('runLogistics error:', err);
        alert('Logistics engine failed. Check backend logs.');
      } finally {
        this.loading = false;
      }
    },

    async saveChanges() {
      alert('Save endpoint not wired yet.');
      this.hasUnsavedChanges = false;
    },

    async onLockClicked(truck) {
      if (!truck || !truck.truck_id) return;

      if (truck.locked) {
        return await this.unlockTruck(truck);
      } else {
        return await this.saveAndLockTruck(truck);
      }
    },

    async saveAndLockTruck(truck) {
      try {
        this.loading = true;

        console.log('[Logistics] saveAndLockTruck CLICKED:', {
          truck_id: truck?.truck_id,
          ship_dt: truck?.ship_dt,
          locked: truck?.locked,
          ordersCount: (truck?.orders || []).length,
          ordNos: (truck?.orders || []).map(o => o.ord_no)
        });

        const ok = confirm(
          `LOCK ${truck.truck_id}?\n\n` +
          `This will SAVE the current order list on the truck and prevent changes during the next Pablo run.`
        );
        if (!ok) return;

        const location = String(this.selectedLocation || '')
          .trim()
          .toUpperCase()
          .slice(0, 1);

        if (!['F','D','K','L'].includes(location)) {
          alert('Invalid location. Cannot lock.');
          return;
        }

        // ✅ Gather current order numbers
        const ordNos = (truck.orders || [])
          .map(o => String(o.ord_no).trim())
          .filter(Boolean);

        console.log('[Logistics] ordNos being SAVED:', ordNos);

        if (ordNos.length === 0) {
          alert(
            `This truck has 0 orders at save-time.\n\n` +
            `That means the drag/drop did not actually update truck.orders.\n\n` +
            `Look in console logs: truck.orders is empty.\n\n` +
            `We will fix drag/drop state next.`
          );
          return;
        }

        // ✅ Normalize ship_dt (must be YYYY-MM-DD)
        let ship_dt = String(truck.ship_dt || '').trim();

        // if ISO timestamp
        if (ship_dt.includes('T')) ship_dt = ship_dt.slice(0, 10);

        // fallback derive from truck_id
        if (!ship_dt || ship_dt.length !== 10) {
          const parts = String(truck.truck_id || '').split('-');
          if (parts.length >= 4) {
            ship_dt = `${parts[1]}-${parts[2]}-${parts[3]}`;
          }
        }

        // ✅ Final validation
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ship_dt)) {
          alert(`Invalid ship_dt being sent: "${ship_dt}"`);
          console.error("🚨 BAD ship_dt", { ship_dt, truck });
          return;
        }

        console.log("[saveAndLockTruck] payload", {
          location,
          ship_dt,
          truck_id: truck.truck_id,
          ord_nos: ordNos
        });

        // ✅ 1) Save the current truck assignments to DB
        const saveResp = await axios.post('/saveTruckOrdersForTruck', {
          location,
          ship_dt,
          truck_id: truck.truck_id,
          ord_nos: ordNos
        });

        console.log('[Logistics] saveTruckOrdersForTruck response:', saveResp.data);

        // ✅ 2) Lock the truck
        const lockResp = await axios.post('/setTruckLock', {
          location,
          ship_dt,
          truck_id: truck.truck_id,
          locked: true
        });

        console.log('[Logistics] setTruckLock response:', lockResp.data);

        // ✅ Clear draft marker
        if (this.pendingTrucks) {
          delete this.pendingTrucks[truck.truck_id];
        }

        // ✅ Reload fresh from DB
        await this.loadAll();

        console.log(`[Logistics] Truck locked and saved: ${truck.truck_id}`);

      } catch (err) {
        console.error('saveAndLockTruck error:', err);
        alert('Failed to save + lock truck. See console.');
      } finally {
        this.loading = false;
      }
    },


    async unlockTruck(truck) {
      try {
        this.loading = true;

        const ok = confirm(
          `UNLOCK ${truck.truck_id}?\n\n` +
          `This will allow edits and the truck WILL be modified by the next Pablo run.`
        );
        if (!ok) return;

        const location = String(this.selectedLocation || '')
          .trim()
          .toUpperCase()
          .slice(0, 1);

        if (!['F','D','K','L'].includes(location)) {
          alert('Invalid location. Cannot unlock.');
          return;
        }

        await axios.post('/setTruckLock', {
          location,
          truck_id: truck.truck_id,
          locked: false
        });

        await this.loadAll();

        console.log(`[Logistics] Truck unlocked: ${truck.truck_id}`);
      } catch (err) {
        console.error('unlockTruck error:', err);
        alert('Failed to unlock truck. See console.');
      } finally {
        this.loading = false;
      }
    },

    onTrucksDragOver(e) {
      const el = this.$refs.trucksScrollEl;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const edge = 70;
      const speed = 18;

      if (y < edge) {
        el.scrollTop -= speed;
      } else if (y > rect.height - edge) {
        el.scrollTop += speed;
      }
    },

    onUnplannedDragOver(e) {
      const el = this.$refs.unplannedScrollEl;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const edge = 70;
      const speed = 18;

      if (y < edge) {
        el.scrollTop -= speed;
      } else if (y > rect.height - edge) {
        el.scrollTop += speed;
      }
    },



    openTruckModal(truck) {
      this.selectedTruck = truck;
      this.showTruckModal = true;
    },

    async openOrderModal(order) {
      console.log("✅ openOrderModal called. Raw order object:", order);

      this.selectedOrder = order;
      this.showOrderModal = true;
      console.log("✅ selectedOrder BEFORE getOrderDetails:", this.selectedOrder);


      // ✅ Reset details
      this.orderDetailsLoading = true;
      this.orderDetailsLines = [];

      try {
        const { data } = await axios.get('/getOrderDetails', {
          params: {
            location: this.selectedLocation,
            ord_no: order.ord_no
          }
        });

        this.orderDetailsLines = data.lines || [];
        console.log("✅ getOrderDetails response data:", data);


        // ✅ Patch customer number into selectedOrder if missing
        // (route may return cust_no or cus_no depending on what you output)
        if (!this.selectedOrder.cust_no && data.cust_no) {
          this.selectedOrder.cust_no = data.cust_no;
        }
        if (!this.selectedOrder.cust_no && data.cus_no) {
          this.selectedOrder.cust_no = data.cus_no;
        }
        if (!this.selectedOrder.cust_no && this.selectedOrder.cus_no) {
          this.selectedOrder.cust_no = this.selectedOrder.cus_no;
        }

        console.log("✅ selectedOrder AFTER patching cust_no:", this.selectedOrder);


        // ✅ Load Standing Order Notes (customer notes)
        await this.loadCustomerOrderNotes();

      } catch (err) {
        console.error('openOrderModal details fetch error:', err);
        this.orderDetailsLines = [];
      } finally {
        this.orderDetailsLoading = false;
      }
    },


    async loadCustomerOrderNotes() {
      console.log("✅ loadCustomerOrderNotes() called");
      console.log("selectedOrder at notes load:", this.selectedOrder);

      const custNo = this.selectedOrder?.cust_no || this.selectedOrder?.cus_no;
      console.log("✅ Notes request custNo:", custNo);


      if (!custNo) {
        console.warn("No customer number available on selectedOrder.");
        this.customerOrderNotes = null;
        return;
      }

      this.customerOrderNotesLoading = true;
      this.customerOrderNotes = null;

      try {
        console.log("➡️ Calling /getCustomerOrderNotes with:", { cust_no: custNo });

        const { data } = await axios.get('/getCustomerOrderNotes', {
          params: {
            location: this.selectedLocation,
            cust_no: custNo
          }
        });

        this.customerOrderNotes = data.notes || null;

      } catch (err) {
        console.error("Error loading customer order notes:", err);
        this.customerOrderNotes = { error: true };
      } finally {
        this.customerOrderNotesLoading = false;
      }
    },


    async askProductionForItem(line) {
      try {
        const payload = {
          loc: this.selectedLocation,
          sender_dept: "Logistics",
          recipient_dept: "Production",
          request_type: "READY_BY_DATE",
          item_no: line.item_no,
          payload: {
            item_no: line.item_no,
            req_ship_dt: this.selectedOrder?.ship_dt || this.selectedOrder?.orig_req_ship_dt || null,
            orig_req_ship_dt: this.selectedOrder?.orig_req_ship_dt || null,
            order_nos: [String(this.selectedOrder?.ord_no || "").trim()],
            shortage_qty: Number(line.shortage) || 0
          },
          requires_replan: false
        };

        const { data } = await axios.post('/pablotify/request', payload);

        console.log('✅ Pablotify request created', data);

        // ✅ Optional: quick user feedback
        alert(`Sent to Production: ${line.item_no}`);

      } catch (err) {
        console.error('askProductionForItem error:', err);
        alert('Failed to send request to Production.');
      }
    },


    closeTruckModal() {
      this.showTruckModal = false;
      this.selectedTruck = null;
    },

    closeOrderModal() {
      this.showOrderModal = false;
      this.selectedOrder = null;
    }

  },

  mounted() {
    this.loadAll();
  }
};
</script>

<style scoped>
/* ✅ Match JIMScheduling look & feel */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #cbbfbf;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #0b0101;
  min-height: 100vh;
  padding-bottom: 40px;
}

/* ✅ LogisticsPage override: DO NOT center the whole page */
.jim-scheduling-container {
  align-items: stretch !important;
  text-align: left !important;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

/* ✅ FULL-WIDTH WRAPPER (required for new layout) */
.logistics-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* ✅ NEW MAIN LAYOUT: left control panel + right scheduler */
.logistics-layout {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* ✅ Left-side button panel */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  color: #fff;
  margin-top: 80px;
}

/* ✅ Right-side wrapper */
.scheduler-wrapper {
  width: 100%;
  min-width: 0;
}

/* ✅ Trucks + Unplanned side-by-side */
.scheduler-grid {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: flex-start;
}

/* ✅ allow flex children to shrink properly */
.scheduler-grid .machines-section,
.scheduler-grid .unscheduled-section {
  min-width: 0;
}

/* ✅ Your OLD .machines-section and .unscheduled-section rules (beautiful sizing) */
.machines-section {
  flex: 3 1 0;
  min-width: 0;
}

.unscheduled-section {
  flex: 1 1 0;
  margin-left: 0;
  min-width: 0;
}

/* ✅ Controls / header section */
.controls-wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin-bottom: 12px;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control select,
.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  min-width: 180px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* ✅ Buttons in Pablo style */
.btn,
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 125px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.btn:hover:not(:disabled),
.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.btn:disabled,
.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.btn-small {
  width: auto;
  padding: 6px 12px;
  font-size: 13px;
}

/* ✅ Panel styling (dark Pablo panels) */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;   
  gap: 18px;                        
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background: rgb(67, 67, 67);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

/* Each metric */
.summary-item {
  min-width: 140px;
  flex: 1 1 140px;                  
  display: flex;                    
  flex-direction: column;
  align-items: center;              
  text-align: center;               
}

/* Label */
.summary-label {
  font-size: 18px;
  opacity: 0.8;
  line-height: 1.1;
}

/* Value */
.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #cbbfbf;
  line-height: 1.1;
  margin-top: 2px;                  /* ✅ tight spacing under label */
}


/* ✅ Day box / trucks container */
.day-box {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
}

/* ✅ Truck row styling */
.truck-row {
  border: 1px solid #333;
  border-radius: 10px;
  margin-bottom: 10px;
  background-color: rgb(67, 67, 67);
  overflow: hidden;
}


.truck-row.locked {
  opacity: 0.85;
  background: #120505;
}

/* Truck header */
.truck-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #333;
}

.truck-id {
  font-weight: bold;
  font-size: 14px;
  min-width: 160px;
  color: #cbbfbf;
}

.truck-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.pill {
  font-size: 12px;
  padding: 4px 9px;
  border-radius: 999px;
  background: #111827;
  border: 1px solid #333;
  color: #e5e7eb;
}

/* ✅ Load bar */
.truck-load-bar {
  height: 10px;
  background: #1f2933;
  position: relative;
}

.truck-load-fill {
  height: 100%;
  background: #56d364;
  width: 0%;
  transition: width 250ms ease;
}

/* ✅ Orders inside truck */
.truck-orders {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
}

.order-card {
  width: 230px;
  border: 1px solid #333;
  border-radius: 10px;
  padding: 10px;
  background: #111827;
  cursor: pointer;
  transition: transform 0.12s ease;
}

.order-card:hover {
  transform: translateY(-2px);
}

.order-top {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 13px;
  color: #e5e7eb;
}

.order-mid {
  margin-top: 6px;
  font-size: 12px;
  color: #e5e7eb;
}

.order-mid .cust {
  font-weight: bold;
}

.order-mid .city {
  opacity: 0.75;
}

.order-bottom {
  margin-top: 6px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  font-size: 11px;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid #333;
  background: #0b0101;
  color: #e5e7eb;
}

.chip.danger {
  background: #2a0b0b;
  border-color: #aa4444;
}

/* ✅ Unplanned panel (keeps your old styling) */
.unplanned-panel {
  background-color: #7affe276;
  border: 1px solid #000;
  border-radius: 10px;
  padding: 12px;
  position: sticky;
  top: 12px;
  max-height: 78vh;
  overflow-y: auto;
}



.unplanned-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}

.unplanned-header h2 {
  font-size: 18px;
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  color: #cbbfbf;
}

.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.unplanned-list {
  overflow-y: auto;
  max-height: calc(78vh - 120px);
  padding-right: 6px;
}

.unplanned-card {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 5px;              /* ✅ match Production */
  background: #f5f8f79a;
  cursor: pointer;
  margin-bottom: 10px;

  font-family: Garamond, serif; /* ✅ match */
  font-size: 12px;              /* ✅ match */
  color: #0b0101;               /* ✅ readable */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.unplanned-card * {
  color: inherit;
}

.unplanned-card .chip {
  background: #ffffff66;   /* semi-light like Production */
  border: 1px solid #ccc;
  color: #0b0101;
}

.unplanned-card .chip.danger {
  background: #ffb3b366;
  border-color: #aa4444;
  color: #0b0101;
}

.unplanned-top,
.unplanned-mid,
.unplanned-bottom {
  width: 100%;
}

.unplanned-mid {
  margin-top: 4px;
  line-height: 1.1;
}

.unplanned-bottom {
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}



.empty-unplanned,
.empty-truck {
  opacity: 0.6;
  font-size: 12px;
  padding: 10px;
  color: #cbbfbf;
}

/* ✅ Lock button */
.lock-btn {
  width: 36px;
  height: 29.45px;
  border-radius: 10px;
  border: 1px solid #444;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
}

.lock-green {
  background: #2ea043;
  color: white;
}

.lock-red {
  background: #c53030;
  color: white;
}

.lock-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ✅ Left truck column scroll */
.trucks-scroll-container {
  max-height: 78vh;
  overflow-y: auto;
  padding: 8px;
  border-radius: 12px;
}

/* ✅ Compact order row */
.order-card-compact {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 8px 10px;
  background: #5595b7c2;
  cursor: pointer;
  transition: transform 0.12s ease;
  font-size: 13px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.1;
  margin-bottom: 6px;
  border-color:#006400;
  justify-content: space-between;
}

.order-left {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.order-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto; /* don't stretch */
}

.order-right {
  flex: 0 0 auto;
  white-space: nowrap;
}


.order-card-compact:hover {
  transform: translateY(-2px);
}

.order-card-compact .ord-no {
  font-weight: bold;
  white-space: nowrap;
}

.order-card-compact .dot {
  opacity: 0.65;
  font-weight: bold;
}

.order-card-compact .city {
  flex: 1;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-card-compact .pallets {
  font-weight: bold;
  white-space: nowrap;
}

/* ✅ Popup modal styling (matches JIMScheduling / MachineScheduling) */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

/* ✅ Header + close button (same vibe as your other modals) */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

/* ✅ Nice table formatting */
.popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.popup-table th,
.popup-table td {
  border: 1px solid #333;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}

.popup-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
}

.popup-table tr:nth-child(even) td {
  background: #0f141c;
}

/* ✅ Ship-block badge (SHORT / HOLD) */
.ship-flag {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  font-weight: bold;
  white-space: nowrap;
  margin-left: 8px;
}

/* ✅ Modal header improvements */
.modal-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-title-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-subtitle {
  font-size: 12px;
  opacity: 0.8;
  color: #cbbfbf;
}

/* ✅ HOLD status styling */
.hold-status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #2a0b0b;
  border: 1px solid #aa4444;
  color: #ff5c5c;
  font-weight: bold;
  margin-top: 6px;
}

/* ✅ Order meta rows: readable but not table-like */
.order-meta > div {
  padding: 6px 0;
  border-bottom: 1px solid #333;
  font-size: 13px;
}

.order-meta > div:last-child {
  border-bottom: none;
}

.order-note {
  font-style: italic;
  opacity: 0.85;
  margin-left: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  display: inline-block;
}


.order-note-input {
  width: 750px;
  padding: 2px 8px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 12px;
  text-align: center;
  background: #fff;
}

.order-note-input::placeholder {
  opacity: 0.5;
}

.order-note-input:focus {
  outline: none;
  border-color: #999;
  background: #fff;
}



</style>
